import React from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Typography } from '@mui/material';

import DetailsLoader from 'ui/DetailsLoader';

import Record from 'ui/Record/Record';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField/CustomUserDropdownField';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import DataError from 'containers/Error/DataError';

import { isNotFound } from 'utils/api/errors';
import API from 'utils/api';
import { UuidableName } from 'utils/api/types';

import { useStore } from 'utils/hooks/store';

import { useNavBarHeight } from 'utils/hooks/navbar';

import CustomModal from 'components/CustomModal';

import UserBackgroundInformation from './components/UserBackgroundInformation';
import UserProfileHeader from './components/UserProfileHeader';

import { ModalText } from './styles';

const UserDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const { userDetails } = useStore();
  const navigate = useNavigate();
  const params = useParams<{ userId: string }>();

  const [deletionModalOpen, setDeletionModalOpen] = React.useState(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [deleting, setDeleting] = React.useState(false);

  // Document ownership state
  const [ownedDocCount, setOwnedDocCount] = React.useState(0);
  const [loadingDocCount, setLoadingDocCount] = React.useState(false);
  const [newOwner, setNewOwner] = React.useState<UuidableName | null>(null);

  React.useEffect(() => {
    if (!deletionModalOpen || !userDetails.details?.uuid) return;
    setLoadingDocCount(true);
    setNewOwner(null);
    API.entities
      .getOwnedDocumentCount(userDetails.details.uuid)()
      .then(({ data }) => setOwnedDocCount(data.count))
      .catch(() => setOwnedDocCount(0))
      .finally(() => setLoadingDocCount(false));
  }, [deletionModalOpen]);

  const handleClose = (): void => {
    setAnchorEl(null);
    setDeletionModalOpen(false);
    setDeleting(false);
  };

  const onDeactivateUser = async (): Promise<void> => {
    const userId = userDetails.details?.uuid as string;
    if (ownedDocCount > 0 && !newOwner) return;

    setDeleting(true);
    try {
      if (ownedDocCount > 0 && newOwner) {
        await API.entities.bulkReassignDocuments(userId, newOwner.uuid)();
      }
      handleClose();
      const response = await userDetails.deleteUser(userId);
      if (response) {
        navigate(-1);
      }
    } catch {
      setDeleting(false);
    }
  };

  return (
    <DetailsLoader params={params} routeParam="userId" store={userDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }

        if (error) {
          if (isNotFound(error)) {
            return <NotFound />;
          }
          return <DataError onReload={reload} />;
        }

        return (
          <Record
            navBarHeight={navBarHeight}
            disableMaxContentHeight
            Content={() => (
              <>
                <UserBackgroundInformation />
                <CustomModal
                  openModal={deletionModalOpen}
                  onClose={handleClose}
                  modalTitle="Deactivate User"
                  modalWidth="sm"
                  cancelButton={
                    <Button
                      disabled={deleting}
                      onClick={handleClose}
                      autoFocus
                      id="cancel-button"
                      variant="outlined"
                      size="large"
                    >
                      Cancel
                    </Button>
                  }
                  submitButton={
                    <Button
                      onClick={onDeactivateUser}
                      disabled={deleting || loadingDocCount || (ownedDocCount > 0 && !newOwner)}
                      id="confirm-button"
                      variant="contained"
                      size="large"
                      color="error"
                    >
                      Deactivate User
                    </Button>
                  }
                >
                  <ModalText>
                    {userDetails.details?.devices?.length && userDetails.details?.devices?.length > 0
                      ? 'Are you sure you want to deactivate this user? ' +
                        'This user currently has a lone worker device or mobile app enabled, ' +
                        'which will be unassigned upon deactivation.'
                      : 'Are you sure you want to deactivate this user?'}
                  </ModalText>

                  {loadingDocCount ? (
                    <CenteredCircularProgress />
                  ) : (
                    ownedDocCount > 0 && (
                      <>
                        <Typography variant="body2" color="warning.main" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                          This user owns {ownedDocCount} document{ownedDocCount > 1 ? 's' : ''}. Select a new owner:
                        </Typography>
                        <CustomUserDropdownField
                          value={newOwner}
                          onChange={(value) => setNewOwner(value as UuidableName | null)}
                          requiredNotFilled={false}
                          description=""
                          disableInitialLoad
                          licenses="documents"
                          excludeUuid={userDetails.details?.uuid}
                        />
                      </>
                    )
                  )}
                </CustomModal>
              </>
            )}
            Header={
              <UserProfileHeader
                setDeletionModalOpen={setDeletionModalOpen}
                setAnchorEl={setAnchorEl}
                anchorEl={anchorEl}
              />
            }
          />
        );
      }}
    </DetailsLoader>
  );
};

export default UserDetails;
